import type {
    GetRecommendationsParams,
    GetSpeciesPropensityPropensitiesParams,
    StoreSearchParams,
} from '@libs/common-types';
import { getLocale, Locale, Stores } from '@libs/core';

const CONSUMER_HEADER_KEY = 'x-petm-consumer';

const buildOrderQuery = ({ email, locale }: { email?: string | null; locale: string }) => {
    return email
        ? `?email=${encodeURIComponent(email.trim().replaceAll(' ', '+'))}&locale=${locale}`
        : `?locale=${locale}`;
};

export const CWEA_ENDPOINTS = {
    account: {
        anonymousId: `account/anonymousId`,
        accounts: 'account/accounts',
        fraudValidationNeeded: 'account/fraudValidationNeeded',
        enrollmentProfile: (key: string) => `account/enrollmentProfile?generateSecuredKey=${key}`,
        logout: 'account/logout',
        refresh: (revokeAll?: boolean) => `account/refresh${revokeAll ? '?revokeAll=true' : ''}`,
        onboarding: 'account/onboarding',
        resendEmailVerificationV2: 'account/resendEmailVerification/v2',
    },
    cart: {
        fetchVetAuthImage: (cartId?: string, imageId?: string) => `carts/${cartId}/items/vetAuth/${imageId}`,
        uploadVetAuthImage: (cartId?: string, itemId?: string) => `carts/${cartId}/items/${itemId}/vetAuth`,
    },
    customerCare: {
        contactUs: `customerCare/contactUs`,
    },
    loyalty: {
        activateOffer: `loyalty/activateOffer`,
        getLoyaltyPoints: `loyalty/points`,
        getOffers: `loyalty/getLoyaltyOffers`,
        getMemberPoints: `loyalty/getMemberPoints`,
        getMemberPointsSoft: `soft/loyalty/getMemberPoints`,
        getPointsHistory: `loyalty/pointsHistory`,
    },
    inventory: {
        getProductInventory: (sku: string) => `inventory/${sku}`,
        getProductInventoryPairs: `inventory`,
    },
    recommendations: {
        getProductRecommendations: ({ productIds, customerId, storeId, locale }: GetRecommendationsParams) =>
            `recommendations/customer/${customerId}/product/${productIds.join(',')}/store/${storeId}/locale/${locale}`,
    },
    speciesPropensity: {
        getPropensities: ({ customDeviceId }: GetSpeciesPropensityPropensitiesParams) =>
            `species-propensity/propensities/${customDeviceId}`,
    },
    stores: {
        getStoreById: (storeId: string) => `stores/v3/${storeId}`,
        getWebStoreById: () => {
            const locale = getLocale();
            const storeId = locale === Locale['en-US'] ? Stores.US : Stores.CA;

            return `stores/v3/web/${storeId}`;
        },
        search: (params: StoreSearchParams) => {
            const locale = getLocale();
            const searchParams = new URLSearchParams({
                locale,
            });

            Object.entries(params).forEach(([key, value]) => {
                if (typeof value !== 'undefined' && value !== null) {
                    searchParams.append(key, String(value));
                }
            });

            const queryString = searchParams.toString();
            return `stores/v3/search?${queryString}`;
        },
    },
    discounts: {
        getDiscountDetails: (key: string) => `discounts/${key}/details`,
    },
    orders: {
        getOrders: `orders`,
        getScheduledOrders: ({ locale }: { locale: string }) => `orders/scheduled?locale=${locale}`,
        getOrder: ({ orderId, email, locale }: { orderId: string; email?: string | null; locale: string }) => {
            return `orders/${orderId}${buildOrderQuery({ email, locale })}`;
        },
        getOrderDeliveryMessage: ({
            orderId,
            email,
            locale,
        }: {
            orderId: string;
            email?: string | null;
            locale: string;
        }) => {
            return `orders/${orderId}/delivery-message${buildOrderQuery({ email, locale })}`;
        },
    },
    pets: {
        getPets: `pets`,
        addPet: `pets`,
        removePet: (petId: string) => `pets/${petId}`,
        editPet: (petId: string) => `pets/${petId}`,
        getPetsConfig: `pets/config`,
        uploadPetPhoto: (petId: string) => `pets/${petId}/photos`,
        updatePetStatus: (petId: string) => `pets/${petId}/status`,
    },
    appointments: {
        getAppointments: `appointments`,
        updateAppointmentStatus: `appointments`,
        getPackages: `appointments/bundles`,
        getCustomerBookingEligibility: `appointments/eligibility`,
        getPetBookingEligibility: (petId: string) => `appointments/eligibility/pets/${petId}`,
    },
    addresses: {
        getAddresses: 'addresses',
        addAddress: 'addresses',
        updateAddress: (addressId: number) => `addresses?addressId=${addressId}`,
        removeAddress: (addressId: number) => `addresses?addressId=${addressId}`,
    },
    product: {
        getProductBase: (id: string, onlineAt?: number) => {
            const searchParams = new URLSearchParams({ locale: getLocale() });
            if (onlineAt !== undefined) searchParams.set('onlineAt', String(onlineAt));
            return `v2/product/${id}/base?${searchParams}`;
        },
        getProductTailored: `v2/product/tailored?locale=${getLocale()}`,
        getProductTailoredSoft: `soft/v2/product/tailored?locale=${getLocale()}`,
        getRelatedProducts: (sku: string) => `v2/product/related/${sku}?locale=${getLocale()}`,
        calculateFrequency: (locale: string, id: string, asFreq57?: boolean) => {
            const path = `v2/product/${locale}/${id}/calculate-frequency`;
            return asFreq57 === undefined ? path : `${path}?asFreq57=${asFreq57}`;
        },
        getAlgoliaProductById: (locale: string, id: string) => `algolia/product/${locale}/${id}`,
        getAlgoliaProductsByIds: (locale: string) => `algolia/products/${locale}`,
    },
    payments: {
        addPayment: 'payments',
        updatePayment: (id: number) => `payments/${id}`,
        removePayment: (cardId: number) => `payments/${encodeURIComponent(cardId)}`,
        adyenMethods: 'payments/adyen-methods',
    },
    buyItAgain: {
        getBuyItAgainRecommendations: `buy-it-again`,
    },
    buyItAgainGuest: {
        getBuyItAgainGuestRecommendations: `buy-it-again-guest`,
    },
    adoptions: {
        getAdoptionCount: `lives-saved`,
    },
    /**
     * DP-2248 product lookup endpoints (cached) proxied by CWEA.
     *
     * NOTE: These routes include locale in the path, matching the upstream Product Catalog Process API contracts.
     */
    productLookup: {
        getProductById: (id: string) => `algolia/product/${getLocale()}/${id}`,
        getProductsByIds: () => `algolia/products/${getLocale()}`,
        getProductVariantsById: (id: string) => `algolia/product/variants/${getLocale()}/${id}`,
    },
    favorites: {
        get: (storeId: string | null) => {
            const base = `favorites?locale=${getLocale()}`;
            if (storeId == null || storeId === '') {
                return base;
            }
            return `${base}&storeId=${encodeURIComponent(storeId)}`;
        },
        post: ({ sku }: { sku: string }) => `favorites/${sku}?locale=${getLocale()}`,
        delete: ({ sku }: { sku: string }) => `favorites/${sku}?locale=${getLocale()}`,
    },
    treatPoints: {
        get: () => `loyalty/points/estimate`,
    },
    pointsPayout: {
        get: () => `loyalty/pointsPayout`,
    },
    subscriptions: {
        getOrders: () => `subscription/orders`,
        getSubscriptionOrder: (subscriptionId: string) => `subscription/subscriptions/${subscriptionId}/order`,
        getSubscriptionShippingAddress: (subscriptionId: string) =>
            `subscription/subscriptions/${subscriptionId}/shipping-address`,
        sendNow: (orderId: string) => `subscription/orders/${orderId}/send-now`,
        reactivate: (subscriptionId: string) => `subscription/subscriptions/${subscriptionId}/reactivate`,
        getProductVariants: () => `subscription/products`,
        updateProductQuantity: (subscriptionId: string) => `subscription/subscriptions/${subscriptionId}/quantity`,
        updateOneTimeProductQuantity: (productId: string) => `subscription/items/${productId}/quantity`,
        updateProduct: (subscriptionId: string) => `subscription/subscriptions/${subscriptionId}/product`,
        updateFrequency: (subscriptionId: string) => `subscription/subscriptions/${subscriptionId}/frequency`,
        updateShippingAddressForOrdersAndSubscriptions: () => `subscription/shipping-address`,
        updateShippingAddressForAll: () => `subscription/orders/shipping-address-for-all`,
        cancelSubscription: (subscriptionId: string) => `subscription/subscriptions/${subscriptionId}/cancel`,
        setPlaceDate: (orderId: string) => `subscription/orders/${orderId}/place-date`,
        cancelOrder: (orderId: string) => `subscription/orders/${orderId}/cancel`,
        getOrderItems: (orderId: string) => `subscription/orders/${orderId}/items`,
        skipItem: (itemId: string) => `subscription/items/${itemId}`,
    },
    profile: {
        getProfile: `profiles`,
        getProfileSoft: `soft/profiles`,
    },
    autoship: {
        getCustomerSubscriptions: () => `autoship?locale=${getLocale()}`,
        getCustomerSubscriptionsSoft: () => `soft/autoship?locale=${getLocale()}`,
    },
};

export const IDENTITY_ENDPOINTS = {
    consumeExchangeToken: 'v2/authentication/consumeExchangeToken',
    createExchangeToken: 'authentication/createExchangeToken',
    resetPassword: (consumerHeaderValue: string) =>
        `v2/authentication/resetPassword?${CONSUMER_HEADER_KEY}=${consumerHeaderValue}`,
    changePassword: (consumerHeaderValue: string) =>
        `authentication/changePassword?${CONSUMER_HEADER_KEY}=${consumerHeaderValue}`,
    logout: 'authentication/logOut',
};
